import { Divider, MyLocationIcon, useTheme } from '@hermes/web-components'
import { ChangeEvent, useContext, useMemo } from 'react'
import useTranslateMessage from '../../../hooks/useTranslateMessage'
import { SearchProfilesPageContext } from '../../../providers/SearchProfilesPage'
import { MobileResultAutocomplete as MobileResultAutocompleteAlternative } from '../../SearchPage/Alternative/AutocompleteResultsModal/MobileResultAutocomplete'
import { MobileResultAutocomplete } from '../../SearchPage/Default/AutocompleteResultsModal/MobileResultAutocomplete'
import {
  ItemRootContainer,
  ItemTitle,
  RootContainer,
  SearchResultsModalRoot
} from '../../SearchPage/Default/AutocompleteResultsModal/styles'
import { AppDataContext } from '../../../providers/AppData'
import { TENANTS } from '../../../constants/global'
import { useFeatureFlags } from '../../../hooks/useFeatureFlags'

type MobileSearchLocationModalProps = {
  isLocationFocused: boolean
  onLocationBlur: () => void
  getYourLocation: () => void
  onChangeLocationSearch: (ev: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void
}

const MobileSearchLocationModal = ({
  isLocationFocused,
  onLocationBlur,
  getYourLocation,
  onChangeLocationSearch
}: MobileSearchLocationModalProps) => {
  const translate = useTranslateMessage()
  const { language, locale } = useContext(AppDataContext)
  const { alternativeSearchEnabled } = useFeatureFlags()
  const theme = useTheme()
  const {
    changeCurrentLocation,
    buildUrl,
    state: { locations, filters }
  } = useContext(SearchProfilesPageContext)

  const onLocationChanged = (label: string, id: string, slug?: string | null) => () => {
    changeCurrentLocation(id, label, slug)
    onLocationBlur()
  }

  const ComponentForResultAutocomplete = useMemo(() => {
    if (locale === TENANTS.WPA || !alternativeSearchEnabled) {
      return MobileResultAutocomplete
    }
    return MobileResultAutocompleteAlternative
  }, [locale, alternativeSearchEnabled])

  return isLocationFocused ? (
    <SearchResultsModalRoot>
      <ComponentForResultAutocomplete
        heading={translate('locations')}
        searchString={decodeURIComponent(filters.location)}
        isOpen={isLocationFocused}
        onClose={onLocationBlur}
        onChangeSearch={onChangeLocationSearch}
      >
        <RootContainer>
          <ItemRootContainer sx={{ justifyContent: 'space-between', alignItems: 'center' }} onClick={getYourLocation}>
            <ItemTitle>{translate('my_current_location')}</ItemTitle>
            <MyLocationIcon sx={{ width: 14, height: 14, color: 'primary.main' }} />
          </ItemRootContainer>
          <ItemRootContainer sx={{ py: 0, minHeight: 0 }}>
            <Divider sx={{ width: '100%' }} />
          </ItemRootContainer>
          {locations?.map(({ description, place_id, slug, googlePlaceId, name }) => {
            const url = buildUrl({
              slug,
              location: description || name[language]
            })
            return (
              <a href={url} style={{ color: 'black', textDecoration: 'none' }} key={place_id || googlePlaceId}>
                <ItemRootContainer
                  onClick={onLocationChanged(description || name[language], place_id || googlePlaceId, slug)}
                >
                  <ItemTitle
                    sx={{
                      ...(!!name && decodeURIComponent(filters?.location) === name[language]
                        ? theme.custom.SearchBar.SelectedMenuItem
                        : {})
                    }}
                  >
                    {description || name[language]}
                  </ItemTitle>
                </ItemRootContainer>
              </a>
            )
          })}
        </RootContainer>
      </ComponentForResultAutocomplete>
    </SearchResultsModalRoot>
  ) : null
}

export default MobileSearchLocationModal
